import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Link as RouterLink } from 'react-router';

import { ContentIcon } from '@/components/atoms';
import { findService, servicePath } from '@/content/services';

import { type ServiceSituation } from '../content';

/**
 * One situation a visitor arrives in, linked to the service that answers it.
 *
 * The situation is the heading and the service is the destination, which is
 * the inversion the band exists for: a catalogue leads with its own nouns and
 * makes the reader translate, and this leads with the reader's sentence. The
 * service's name is still shown, because the link has to say where it goes.
 *
 * Drawn as a row rather than a card. Six cards would be a second grid a scroll
 * below the first one, and the two would read as the same band twice; a row
 * rhythm reads as a list of answers, which is what it is.
 */
export function ServiceSituationRow({ item }: { item: ServiceSituation }) {
  const service = findService(item.service);
  // A situation naming a slug that no longer exists is a content error, not a
  // visitor's problem: drop the row rather than render a broken link. The slug
  // is typed as `ServiceSlug`, so this is unreachable today and stays correct
  // if a service is ever retired from the catalogue without this list.
  if (!service) return null;

  return (
    <Box
      component="li"
      sx={{
        borderBottom: 1,
        borderColor: 'divider',
        '&:first-of-type': { borderTop: 1, borderColor: 'divider' },
      }}
    >
      <Stack
        component={RouterLink}
        to={servicePath(service)}
        prefetch="intent"
        direction="row"
        spacing={{ xs: 4, md: 5 }}
        sx={{
          alignItems: 'flex-start',
          py: { xs: 5, md: 6 },
          textDecoration: 'none',
          color: 'inherit',
          '&:hover .ServiceSituation-title, &:focus-visible .ServiceSituation-title': {
            color: 'primary.main',
          },
          '&:hover .ServiceSituation-arrow, &:focus-visible .ServiceSituation-arrow': {
            transform: 'translateX(4px)',
          },
        }}
      >
        <ContentIcon
          name={item.icon}
          sx={{ flexShrink: 0, mt: 0.5, fontSize: 26, color: 'primary.main' }}
        />

        <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
          {/* The visitor's words are the heading. "Quotation" is not what
              anybody types into a search box; "what will it really cost" is. */}
          <Typography
            className="ServiceSituation-title"
            variant="h6"
            component="h3"
            sx={{ fontSize: '1.0625rem', transition: 'color 200ms ease' }}
          >
            “{item.situation}”
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {item.answer}
          </Typography>
          <Typography
            variant="caption"
            // Was `text.disabled` (2.9:1 at 12px). This line names the
            // destination of the link it sits in, so it is the last caption
            // on the page that should be the hardest to read.
            sx={{ fontWeight: 700, letterSpacing: '0.04em', color: 'text.secondary' }}
          >
            {service.title}
          </Typography>
        </Stack>

        <ArrowForwardRounded
          aria-hidden
          className="ServiceSituation-arrow"
          sx={{
            flexShrink: 0,
            mt: 0.5,
            fontSize: 20,
            color: 'text.disabled',
            transition: 'transform 250ms ease',
          }}
        />
      </Stack>
    </Box>
  );
}
